import Router from "next/router";
import { navigationActions } from "@context/navigation";
import { SITE_ORIGIN } from "@utils/constants";

export const REDIRECT_ALLOWED_DOMAINS = ["faradars.org"] as const;

const AUTH_PATH_PREFIXES = [
  "/register",
  "/login",
  "/signin",
  "/my-account/lost-password",
];

export const isAuthPath = (pathname: string) =>
  AUTH_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );

export const isAllowedRedirectHost = (
  hostname: string,
  allowedDomains: readonly string[] = REDIRECT_ALLOWED_DOMAINS
): boolean => {
  const host = hostname.trim().toLowerCase().replace(/\.$/, "");
  if (!host || host.includes(":") || host.includes(" ")) return false;

  return allowedDomains.some((domain) => {
    const apex = domain.trim().toLowerCase();
    return host === apex || host.endsWith(`.${apex}`);
  });
};

const isSafeRelativePath = (path: string): boolean => {
  if (!path.startsWith("/")) return false;
  else if (path.startsWith("//")) return false;
  else if (path.includes("\\")) return false;
  else if (path.includes("@")) return false;
  else return true;
};

export const resolveAuthRedirectTarget = (
  rawNext: string | undefined | null,
  fallback = "/"
): string => {
  if (!rawNext || typeof rawNext !== "string") return fallback;

  let candidate = rawNext.trim();
  if (!candidate) return fallback;

  try {
    candidate = decodeURIComponent(candidate);
  } catch {
    return fallback;
  }

  candidate = candidate.trim();
  if (!candidate) return fallback;

  if (
    candidate.startsWith("//") ||
    candidate.startsWith("http://") ||
    candidate.startsWith("https://")
  ) {
    try {
      const url = new URL(
        candidate.startsWith("//") ? `https:${candidate}` : candidate
      );

      if (!isAllowedRedirectHost(url.hostname)) return fallback;

      const siteOrigin = new URL(SITE_ORIGIN).origin;

      if (url.origin === siteOrigin) {
        if (isAuthPath(url.pathname)) return fallback;
        return `${url.pathname}${url.search}${url.hash}` || fallback;
      }

      return url.href;
    } catch {
      return fallback;
    }
  }

  if (!isSafeRelativePath(candidate)) return fallback;

  const pathname = candidate.split("?")[0].split("#")[0] || "/";
  if (isAuthPath(pathname)) return fallback;

  return candidate;
};

export const consumeAuthRedirect = (
  fallback = "/",
  clearStore = true
): string => {
  const fromQuery =
    typeof Router.query.next === "string" ? Router.query.next : "";
  const fromStore = navigationActions.getNext(clearStore);
  return resolveAuthRedirectTarget(fromQuery || fromStore, fallback);
};
